import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/Layout";
import { Lock, ArrowRight, CheckCircle } from "lucide-react";
import logo from "@/assets/velocity-logo-light.png";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [expired, setExpired] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // The auth client consumes the recovery token from the URL on load, so we
    // wait for the PASSWORD_RECOVERY event / session rather than reading the hash.
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    if (params.get("error")) {
      setExpired(true);
      return;
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") && session) setReady(true);
    });
    const code = new URLSearchParams(window.location.search).get("code");
    if (code) {
      supabase.auth.exchangeCodeForSession(code).then(({ data, error }) => {
        if (error || !data.session) setExpired(true);
        else setReady(true);
      });
    }
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setReady(true);
    });
    const t = setTimeout(() => {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session) setExpired(true);
      });
    }, 4000);
    return () => { subscription.unsubscribe(); clearTimeout(t); };
  }, []);

  const getRedirect = async (userId: string) => {
    const [{ data: isAdmin }, { data: isEmployee }, { data: isAuditor }] = await Promise.all([
      supabase.rpc("has_role", { _user_id: userId, _role: "admin" }),
      supabase.rpc("has_role", { _user_id: userId, _role: "employee" }),
      supabase.rpc("has_role", { _user_id: userId, _role: "auditor" as any }),
    ]);
    if (isAuditor && !isAdmin && !isEmployee) return "/auditor";
    if (isEmployee) return "/employee";
    if (isAdmin) return "/admin";
    return "/portal";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    const { data, error } = await supabase.auth.updateUser({
      password,
      data: { must_change_password: false },
    });
    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
      const dest = data.user ? await getRedirect(data.user.id) : "/client-login";
      setTimeout(() => navigate(dest), 1500);
    }
    setLoading(false);
  };

  return (
    <Layout>
      <div className="min-h-[80vh] flex items-center justify-center relative overflow-hidden" style={{ background: 'linear-gradient(160deg, hsl(218 55% 10%) 0%, hsl(215 45% 16%) 50%, hsl(215 35% 26%) 100%)' }}>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-teal/8 to-transparent rounded-bl-full" />
        <div className="relative z-10 w-full max-w-md mx-6 py-20">
          <div className="text-center mb-10">
            <a href="/"><img src={logo} alt="Velocity Offshore" className="h-14 mx-auto mb-6 opacity-90" /></a>
            <h1 className="font-display text-3xl text-primary-foreground tracking-tight mb-2">
              Reset <span className="accent-text">Password</span>
            </h1>
          </div>

          <div className="glass-card p-8 rounded-2xl">
            {success ? (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-teal mx-auto mb-4" />
                <p className="text-primary-foreground font-body">Password updated! Redirecting...</p>
              </div>
            ) : expired && !ready ? (
              <div className="text-center py-4 space-y-4">
                <p className="text-primary-foreground font-body text-sm">This reset link is invalid or has expired. Please request a new one.</p>
                <a href="/employee-login" className="block text-teal text-sm font-bold hover:underline">Intranet sign in</a>
                <a href="/client-login" className="block text-teal text-sm font-bold hover:underline">Client portal sign in</a>
              </div>
            ) : !ready ? (
              <p className="text-center py-4 text-primary-foreground/80 font-body text-sm">Verifying your reset link…</p>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/50 mb-2 font-body">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-teal/50" />
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-11 pr-4 py-3.5 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 focus:ring-1 focus:ring-teal/20 transition-all" placeholder="••••••••" required />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/50 mb-2 font-body">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-teal/50" />
                    <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full pl-11 pr-4 py-3.5 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 focus:ring-1 focus:ring-teal/20 transition-all" placeholder="••••••••" required />
                  </div>
                </div>
                {error && <div className="px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm font-body">{error}</div>}
                <button type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-xl hover:shadow-teal/25 transition-all duration-300 font-body disabled:opacity-50">
                  {loading ? "Updating..." : "Update Password"}
                  {!loading && <ArrowRight className="w-4 h-4" />}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ResetPassword;
